import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Delete from '@mui/icons-material/Delete';
import { Add } from '@mui/icons-material';

const AssessmentSection = ({
    onAddQuestion,
    displayAssessments,
    handleDeleteAssessment,
    theme,
}) => (
    <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
                <AssignmentIcon
                    sx={{
                        color: theme.palette.secondary.main,
                    }}
                />
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 600,
                        color: 'text.secondary',
                    }}
                >
                    Interactive Video Assessments
                </Typography>
            </Box>
            <Chip
                label={`${displayAssessments?.length || 0} Question${
                    displayAssessments?.length !== 1 ? 's' : ''
                }`}
                color={displayAssessments?.length > 0 ? 'secondary' : 'default'}
                size="small"
                sx={{
                    fontWeight: 600,
                    borderRadius: '6px',
                }}
            />
        </Box>

        {!displayAssessments || displayAssessments.length === 0 ? (
            <EmptyAssessmentState theme={theme} />
        ) : (
            <Stack spacing={2.5}>
                {displayAssessments.map((item, idx) => (
                    <AssessmentCard
                        key={item?._id || idx}
                        item={item}
                        idx={idx}
                        handleDeleteAssessment={handleDeleteAssessment}
                        theme={theme}
                    />
                ))}
            </Stack>
        )}

        <Box display="flex" justifyContent="flex-end" mt={1}>
            <Button
                variant="outlined"
                color="secondary"
                startIcon={<Add />}
                onClick={onAddQuestion}
                sx={{
                    py: 1,
                    px: 3,
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    textTransform: 'none',
                }}
            >
                Add Assessment
            </Button>
        </Box>
    </Box>
);

const EmptyAssessmentState = ({ theme }) => (
    <Box
        sx={{
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: '12px',
            p: 4,
            textAlign: 'center',
            backgroundColor: theme.palette.grey[50],
        }}
    >
        <Typography variant="body2" color="text.secondary">
            No video assessments added yet. Click "Add Video Assessment" to
            create one.
        </Typography>
    </Box>
);

const getAnswerDisplay = (item) => {
    if (!item?.answer) return '';
    const answer = item.answer;
    const optionLabels = {
        option1: 'Option A',
        option2: 'Option B',
        option3: 'Option C',
        option4: 'Option D',
    };
    if (optionLabels[answer]) {
        const optionText = item.options?.[answer] || '';
        return `${optionLabels[answer]}${optionText ? `: ${optionText}` : ''}`;
    }
    return answer;
};

const AssessmentCard = ({ item, idx, handleDeleteAssessment, theme }) => (
    <Card
        variant="outlined"
        sx={{
            borderRadius: '12px',
            borderColor: theme.palette.divider,
            boxShadow: theme.shadows[1],
            position: 'relative',
            '&:hover': {
                boxShadow: theme.shadows[2],
            },
        }}
    >
        <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                gap={2}
                mb={2}
            >
                <Box>
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.secondary.main,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}
                    >
                        Question {idx + 1}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mt: 0.5 }}
                    >
                        {item.question}
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    color="error"
                    onClick={() =>
                        handleDeleteAssessment(item._id || item.id, idx)
                    }
                >
                    <Delete fontSize="small" />
                </IconButton>
            </Box>

            <OptionsGrid item={item} theme={theme} />

            <Box mt={2.5} display="flex" justifyContent="flex-end">
                {item.answer && (
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.8,
                            px: 1.5,
                            py: 0.8,
                            borderRadius: '6px',
                            backgroundColor: '#E8F5E9',
                            border: '1px solid #C8E6C9',
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                color: '#2E7D32',
                            }}
                        >
                            Correct Answer: {getAnswerDisplay(item)}
                        </Typography>
                    </Box>
                )}
            </Box>
        </CardContent>
    </Card>
);

const OptionsGrid = ({ item, theme }) => (
    <Grid container spacing={2}>
        {['option1', 'option2', 'option3', 'option4'].map((key) => {
            const val = item.options?.[key];
            if (!val) return null;
            const labels = {
                option1: 'A',
                option2: 'B',
                option3: 'C',
                option4: 'D',
            };
            const optionLetter = labels[key];
            const isCorrect =
                item.answer &&
                val &&
                (item.answer === key ||
                    item.answer.toLowerCase().trim() ===
                        val.toLowerCase().trim());
            return (
                <Grid item xs={12} sm={6} key={key}>
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: '8px',
                            border: `1px solid ${
                                isCorrect ? '#81C784' : theme.palette.divider
                            }`,
                            backgroundColor: isCorrect
                                ? 'rgba(226, 245, 236, 0.5)'
                                : theme.palette.grey[50],
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Chip
                            label={optionLetter}
                            size="small"
                            color={isCorrect ? 'success' : 'default'}
                            sx={{
                                fontWeight: 700,
                                height: '20px',
                                fontSize: '0.75rem',
                                backgroundColor: isCorrect
                                    ? '#4CAF50'
                                    : theme.palette.grey[300],
                                color: isCorrect ? '#fff' : 'text.primary',
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                color: isCorrect ? '#2E7D32' : 'text.primary',
                                fontWeight: isCorrect ? 600 : 400,
                            }}
                        >
                            {val}
                        </Typography>
                    </Box>
                </Grid>
            );
        })}
    </Grid>
);

export default AssessmentSection;
